import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";

import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import { BluffPlayersRoster } from "../../party/BluffPlayersRoster";
import { PartyTopBar } from "../../party/PartyChrome";
import { PartySuggestCategoryDrawer } from "../../party/PartySuggestCategoryDrawer";
import { PartyMascot } from "../../party/PartyMascot";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { parseBluffGamePayload, type BluffGamePayload } from "../../party/answerBluffGamePayload";
import { bluffQaCombinedPhase } from "../../party/answerBluffPhaseHelpers";
import { bluffVoteOptionsShuffleSeed, shuffleBluffVoteOptionsDeterministic } from "../../party/answerBluffVoteShuffle";
import { applyPresenceToRoster, parseBluffPresencePayload, parseBluffSocketReadyPayload, type BluffRoster } from "../../party/answerBluffPresence";
import {
  ANSWER_BLUFF_WS_PING_INTERVAL_MS,
  stringifyBluffWsPing,
  stringifyJoin,
} from "../../party/answerBluffRoomMessages";
import { answerBluffWsUrl, buildAnswerBluffJoinUrl } from "../../party/answerBluffWsUrl";
import {
  BluffLeaderboardList,
  BluffQuestionCard,
  BluffScoresStrip,
  bluffPhaseEmojiLabel,
  bluffVoteOptionButtonSx,
} from "../../party/bluffGameUi";
import {
  bluffAnswerRejectReasonMessage,
  bluffSubmissionMatchesTruth,
  resolveBluffCanonicalTruth,
} from "../../party/bluffTruthAnswer";
import { partyBluffAnswerFieldSx, partyOutlinedFieldSx } from "../../party/partyFieldSx";
import { useBluffPhaseCountdown } from "../../party/useBluffPhaseCountdown";
import type { PartyShellPalette } from "../../theme/theme";
import type { AnswerBluffRoomControlResponse, ModeCategoryRow, ModeQuickQuestionRow } from "../../types/api";
import { apiClient } from "../../utils/api";
import { MixpanelEvents, trackEvent, trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { modeCategoryDisplayName, modeCategoryPrimaryName } from "../../utils/modeCategoryLabels";
import { clearPartyGameCache } from "../../utils/partyGameLocalCache";

const BLUFF_ROUND_MS = 30_000;
const BLUFF_LEADERBOARD_MS = 8_000;
const BLUFF_JOIN_BUFFER_MS = 1_200;
/** Extra ms after `phase_ends_at` so the server has closed the phase before the next control call. */
const BLUFF_PHASE_END_BUFFER_MS = 500;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Align host automation with the same `phase_ends_at` the UI and guests use: the latest
 * server snapshot after each control step (see {@link PartyJoinBluffPage} countdown).
 * Waiting on an older (e.g. question-only) deadline while the UI shows the answer window’s
 * `phase_ends_at` made the host timer and `open_vote` fire early relative to the displayed clock.
 */
function delayUntilBluffPhaseEnd(game: BluffGamePayload | null, fallbackMs: number): Promise<void> {
  const raw = game?.phase_ends_at;
  if (!raw) return delay(fallbackMs);
  const endMs = Date.parse(raw);
  if (!Number.isFinite(endMs)) return delay(fallbackMs);
  const ms = Math.max(0, endMs - Date.now()) + BLUFF_PHASE_END_BUFFER_MS;
  return delay(ms);
}

type RoomState = {
  code: string;
  websocket_path: string;
  questions: ModeQuickQuestionRow[];
};

function normalizeQuestions(raw: unknown[]): ModeQuickQuestionRow[] {
  return raw
    .map((q) => {
      if (!q || typeof q !== "object") return null;
      const o = q as Record<string, unknown>;
      const question =
        (typeof o.question === "string" && o.question.trim()) ||
        (typeof o.prompt === "string" && o.prompt.trim()) ||
        "";
      if (!question) return null;
      const answer =
        typeof o.answer === "string"
          ? o.answer
          : typeof o.correct_answer === "string"
            ? o.correct_answer
            : undefined;
      const id = typeof o.id === "number" ? o.id : undefined;
      return { id, question, answer, sort_order: typeof o.sort_order === "number" ? o.sort_order : undefined };
    })
    .filter(Boolean) as ModeQuickQuestionRow[];
}

function scoreRows(game: BluffGamePayload | null): { name: string; pts: number }[] {
  if (!game) return [];
  const players = game.players;
  const scores = game.scores;
  return Object.keys(scores)
    .map((id) => ({
      name: players[id] ?? id.slice(-8),
      pts: scores[id] ?? 0,
    }))
    .sort((a, b) => b.pts - a.pts);
}

export default function PartyPlayAnswerBluff() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const shareModeTitle = usePartyShareModeTitle("bluff", i18n.language?.startsWith("ar") ? "إجابة أو خدعة" : "Answer or Bluff");
  const fieldSx = useMemo(() => partyOutlinedFieldSx(partyPal), [partyPal]);
  const answerFieldSx = useMemo(() => partyBluffAnswerFieldSx(partyPal), [partyPal]);

  const [room, setRoom] = useState<RoomState | null>(null);
  const setWsLog = useState<string[]>([])[1];
  const [guestRoster, setGuestRoster] = useState<BluffRoster>({});
  const [serverGame, setServerGame] = useState<BluffGamePayload | null>(null);
  const [controlError, setControlError] = useState<string | null>(null);
  const [hostDisplayName, setHostDisplayName] = useState("");
  const [hostGameStarted, setHostGameStarted] = useState(false);
  const [hostAnswerDraft, setHostAnswerDraft] = useState("");
  const [hostVoteSubmitted, setHostVoteSubmitted] = useState(false);
  const [hostAnswerAck, setHostAnswerAck] = useState<string | null>(null);
  const [hostAnswerSubmitLocked, setHostAnswerSubmitLocked] = useState(false);
  const [hostAnswerTruthBlockHint, setHostAnswerTruthBlockHint] = useState<string | null>(null);
  const [joinLinkCopied, setJoinLinkCopied] = useState(false);
  const [suggestCategoryOpen, setSuggestCategoryOpen] = useState(false);
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const hostGameStartedRef = useRef(false);
  const hostJoinSentRef = useRef(false);
  const hostDisplayNameRef = useRef("");
  const hostAutoDashSentForDeadlineRef = useRef<string | null>(null);
  const hostLastAnswerWindowKeyRef = useRef<string>("");
  const completionTrackedRef = useRef(false);
  /** True once we have seen `phaseSecondsLeft > 1` in this QA window (avoids auto "-" if user joins at 1s). */
  const hostAnswerPhaseSawPositiveCountdownRef = useRef(false);
  const serverGameRef = useRef<BluffGamePayload | null>(null);
  /** Filled when the room is created; used when the host taps “Start game”. */
  const bluffGameStartedMetaRef = useRef<{ category_names: string[]; question_count: number } | null>(null);
  const bluffWsPingIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    hostDisplayNameRef.current = hostDisplayName;
  }, [hostDisplayName]);

  useEffect(() => {
    serverGameRef.current = serverGame;
  }, [serverGame]);

  useEffect(() => {
    if (serverGame?.phase === "game_over") {
      clearPartyGameCache("bluff");
    }
  }, [serverGame?.phase]);

  useEffect(() => {
    if (serverGame?.phase !== "game_over" || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "bluff",
      trial: false,
      mode_name_en: "Answer bluff",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [gameStartedAtMs, serverGame?.phase]);

  const joinUrl = useMemo(
    () => (room?.code && room.websocket_path ? buildAnswerBluffJoinUrl(room.code, room.websocket_path) : ""),
    [room?.code, room?.websocket_path]
  );

  /** Guests who have joined via QR (presence); host is not in this list until after Start. */
  const guestJoinCount = useMemo(() => Object.keys(guestRoster).length, [guestRoster]);

  const phaseSecondsLeft = useBluffPhaseCountdown(serverGame?.phase_ends_at);

  const catQ = useQuery({
    queryKey: ["modes", "answer-bluff", "categories"],
    queryFn: () => apiClient.getModeCategories("answer-bluff"),
    enabled: !room,
  });

  const startMut = useMutation({
    mutationFn: ({ categoryId }: { categoryId: number; category_names: string[] }) =>
      apiClient.postAnswerBluffRoomStart(categoryId),
    onMutate: (variables: { categoryId: number; category_names: string[] }) => {
      trackEvent(MixpanelEvents.PARTY_BLUFF_ROOM_START_REQUESTED, { category_id: variables.categoryId });
    },
    onSuccess: (data, variables: { categoryId: number; category_names: string[] }) => {
      const r = data.room as Record<string, unknown>;
      const code =
        (typeof r?.code === "string" && r.code) ||
        (typeof r?.room_code === "string" && r.room_code) ||
        (typeof r?.id === "string" && r.id) ||
        "—";
      const questions = normalizeQuestions((data.questions ?? []) as unknown[]);
      bluffGameStartedMetaRef.current = {
        category_names: variables.category_names,
        question_count: questions.length,
      };
      trackEvent(MixpanelEvents.PARTY_BLUFF_ROOM_CREATED, {
        question_count: questions.length,
        room_code_present: code !== "—",
      });
      setRoom({
        code,
        websocket_path: data.websocket_path,
        questions,
      });
      setServerGame(null);
      setControlError(null);
      setHostGameStarted(false);
      hostGameStartedRef.current = false;
      hostJoinSentRef.current = false;
      setHostAnswerDraft("");
      setHostVoteSubmitted(false);
      setHostAnswerAck(null);
      setHostAnswerSubmitLocked(false);
      setHostAnswerTruthBlockHint(null);
      hostAutoDashSentForDeadlineRef.current = null;
      hostLastAnswerWindowKeyRef.current = "";
      hostAnswerPhaseSawPositiveCountdownRef.current = false;
      invalidateUserSessionCredits(queryClient);
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      trackEvent(MixpanelEvents.PARTY_BLUFF_ROOM_START_FAILED, { status });
      if (status === 402) {
        trackEvent(MixpanelEvents.PARTY_GAME_CREDITS_BLOCKED, { shell_id: "bluff", context: "room_start" });
      }
    },
  });

  useEffect(() => {
    if (room) return;
    trackEvent(MixpanelEvents.PARTY_BLUFF_HOST_LOBBY_VIEWED, {});
  }, [room]);

  useEffect(() => {
    if (!room?.code) return;
    let cancelled = false;
    void apiClient.postAnswerBluffRoomControl(room.code, "state").then((d) => {
      if (cancelled || !d?.game) return;
      const g = parseBluffGamePayload(d.game as unknown);
      if (g) setServerGame(g);
    });
    return () => {
      cancelled = true;
    };
  }, [room?.code]);

  const trySendHostJoin = useCallback((name: string) => {
    if (hostJoinSentRef.current) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !name.trim()) return;
    try {
      ws.send(stringifyJoin(name.trim()));
      hostJoinSentRef.current = true;
    } catch {
      setControlError(t("party.playBluff.joinSendFailed"));
    }
  }, [t]);

  const handleStartHostGame = useCallback(() => {
    const name = hostDisplayName.trim();
    if (!name) return;
    if (guestJoinCount < 1) return;
    const meta = bluffGameStartedMetaRef.current;
    trackPartyGameStarted({
      shell_id: "bluff",
      category_names: meta?.category_names ?? [],
      number_of_players: guestJoinCount + 1,
      trial: false,
      mode_name_en: "Answer bluff",
      deck_size: meta?.question_count ?? room?.questions.length,
      guest_join_count: guestJoinCount,
      room_code_present: Boolean(room?.code),
    });
    trackEvent(MixpanelEvents.PARTY_BLUFF_HOST_SESSION_STARTED, {
      guest_join_count: guestJoinCount,
    });
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    hostGameStartedRef.current = true;
    setHostGameStarted(true);
    setControlError(null);
    hostJoinSentRef.current = false;
    trySendHostJoin(name);
    window.setTimeout(() => trySendHostJoin(name), 200);
    window.setTimeout(() => trySendHostJoin(name), 1200);
  }, [hostDisplayName, trySendHostJoin, guestJoinCount, room?.code, room?.questions.length]);

  useEffect(() => {
    if (!room?.websocket_path) return;
    let stopped = false;
    let reconnectTimer: number | null = null;
    let reconnectAttempt = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    setGuestRoster({});
    hostJoinSentRef.current = false;
    const clearWsPing = () => {
      if (bluffWsPingIntervalRef.current != null) {
        window.clearInterval(bluffWsPingIntervalRef.current);
        bluffWsPingIntervalRef.current = null;
      }
    };
    const startWsPing = () => {
      clearWsPing();
      bluffWsPingIntervalRef.current = window.setInterval(() => {
        const w = wsRef.current;
        if (!w || w.readyState !== WebSocket.OPEN) return;
        try {
          w.send(stringifyBluffWsPing());
        } catch {
          /* ignore */
        }
      }, ANSWER_BLUFF_WS_PING_INTERVAL_MS);
    };

    const connectHostWs = () => {
      if (stopped) return;
      const path = room.websocket_path;
      if (!path) return;
      try {
        const prev = wsRef.current;
        if (prev) {
          try {
            prev.onclose = null;
            prev.close();
          } catch {
            /* ignore */
          }
          wsRef.current = null;
        }
        hostJoinSentRef.current = false;
        const ws = new WebSocket(answerBluffWsUrl(path));
        wsRef.current = ws;
        ws.onmessage = (ev) => {
        const raw = String(ev.data);
        try {
          const data = JSON.parse(raw) as unknown;
          if (data && typeof data === "object" && (data as { type?: unknown }).type === "pong") {
            return;
          }
          const ready = parseBluffSocketReadyPayload(data);
          if (ready) {
            if (hostGameStartedRef.current) {
              trySendHostJoin(hostDisplayNameRef.current);
            }
            return;
          }
          const pres = parseBluffPresencePayload(data);
          if (pres) {
            setGuestRoster((r) => applyPresenceToRoster(r, pres));
            return;
          }
          if (data && typeof data === "object" && (data as { type?: unknown }).type === "ack") {
            const o = data as Record<string, unknown>;
            if (o.event === "answer_rejected" || o.ok === false) {
              setHostAnswerSubmitLocked(false);
              const reason = typeof o.reason === "string" ? o.reason : undefined;
              const msg = bluffAnswerRejectReasonMessage(reason, t);
              if (reason === "cannot_submit_truth") {
                setHostAnswerTruthBlockHint(msg);
                setHostAnswerAck(null);
              } else {
                setHostAnswerTruthBlockHint(null);
                setHostAnswerAck(msg);
              }
            } else if (o.event === "answer_recorded") {
              setHostAnswerDraft("");
              setHostAnswerSubmitLocked(true);
              setHostAnswerTruthBlockHint(null);
              setHostAnswerAck(t("party.joinBluff.answerRecorded"));
            }
            return;
          }
          const game = parseBluffGamePayload(data);
          if (game) {
            setServerGame(game);
            return;
          }
        } catch {
          /* fall through */
        }
        setWsLog((l) => [`← ${raw}`, ...l].slice(0, 30));
      };
      ws.onerror = () => {
        setWsLog((l) => [`← ${t("party.playBluff.logWsError")}`, ...l].slice(0, 30));
      };
      ws.onopen = () => {
        reconnectAttempt = 0;
        clearReconnectTimer();
        startWsPing();
        if (hostGameStartedRef.current) {
          trySendHostJoin(hostDisplayNameRef.current);
          window.setTimeout(() => trySendHostJoin(hostDisplayNameRef.current), 1200);
        }
      };
      ws.onclose = () => {
        clearWsPing();
        wsRef.current = null;
        if (stopped) return;
        clearReconnectTimer();
        const delay = Math.min(30_000, 700 * Math.pow(2, Math.min(reconnectAttempt, 6)));
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          connectHostWs();
        }, delay);
      };
    } catch {
      setWsLog([t("party.playBluff.logWsOpenFailed")]);
      if (!stopped) {
        clearReconnectTimer();
        const delay = Math.min(30_000, 700 * Math.pow(2, Math.min(reconnectAttempt, 6)));
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          connectHostWs();
        }, delay);
      }
    }
    };

    connectHostWs();

    const onVisibility = () => {
      if (stopped || document.visibilityState !== "visible") return;
      const w = wsRef.current;
      if (!w || w.readyState !== WebSocket.OPEN) {
        clearReconnectTimer();
        reconnectAttempt = 0;
        connectHostWs();
      } else {
        try {
          w.send(stringifyBluffWsPing());
        } catch {
          /* ignore */
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped = true;
      clearReconnectTimer();
      document.removeEventListener("visibilitychange", onVisibility);
      clearWsPing();
      const w = wsRef.current;
      wsRef.current = null;
      setGuestRoster({});
      try {
        if (w) {
          w.onclose = null;
          w.close();
        }
      } catch {
        /* ignore */
      }
    };
  }, [room?.websocket_path, trySendHostJoin, t, setWsLog]);

  useEffect(() => {
    if (!serverGame || !hostGameStarted) return;
    if (bluffQaCombinedPhase(serverGame.phase)) {
      const windowKey = `${serverGame.round_index}-${serverGame.phase_ends_at ?? ""}`;
      if (hostLastAnswerWindowKeyRef.current !== windowKey) {
        hostLastAnswerWindowKeyRef.current = windowKey;
        setHostVoteSubmitted(false);
        setHostAnswerAck(null);
        setHostAnswerSubmitLocked(false);
        setHostAnswerTruthBlockHint(null);
        hostAutoDashSentForDeadlineRef.current = null;
        hostAnswerPhaseSawPositiveCountdownRef.current = false;
      }
    } else {
      hostLastAnswerWindowKeyRef.current = "";
      hostAnswerPhaseSawPositiveCountdownRef.current = false;
    }
    if (serverGame.phase === "vote") {
      setHostVoteSubmitted(false);
    }
  }, [serverGame, hostGameStarted]);

  useEffect(() => {
    if (!hostGameStarted || !serverGame || !bluffQaCombinedPhase(serverGame.phase)) return;
    if (phaseSecondsLeft != null && phaseSecondsLeft > 1) {
      hostAnswerPhaseSawPositiveCountdownRef.current = true;
    }
  }, [hostGameStarted, serverGame, phaseSecondsLeft]);

  useEffect(() => {
    if (!hostGameStarted || !serverGame || !bluffQaCombinedPhase(serverGame.phase)) return;
    if (!serverGame.phase_ends_at || phaseSecondsLeft == null || phaseSecondsLeft > 1) return;
    // `useBluffPhaseCountdown` uses ceil → "1" is the last full second before the buzzer; submit then (not at 0).
    if (phaseSecondsLeft !== 1) return;
    if (!hostAnswerPhaseSawPositiveCountdownRef.current) return;
    if (hostAnswerSubmitLocked) return;
    if (hostAnswerDraft.trim()) return;
    if (hostAutoDashSentForDeadlineRef.current === serverGame.phase_ends_at) return;
    hostAutoDashSentForDeadlineRef.current = serverGame.phase_ends_at;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "bluff_answer", text: "-" }));
      trackEvent(MixpanelEvents.PARTY_BLUFF_HOST_ANSWER_AUTO_SUBMITTED, {
        round_index: serverGame.round_index,
      });
    } catch {
      setControlError(t("party.joinBluff.errorConnect"));
    }
  }, [hostGameStarted, serverGame, phaseSecondsLeft, hostAnswerSubmitLocked, hostAnswerDraft, t]);

  useEffect(() => {
    if (!hostGameStarted || !room?.code) return;
    let cancelled = false;
    const code = room.code;

    const apply = (data: AnswerBluffRoomControlResponse): boolean => {
      if (data?.detail) {
        setControlError(typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail));
        return false;
      }
      setControlError(null);
      if (data?.game) {
        const g = parseBluffGamePayload(data.game as unknown);
        if (g) setServerGame(g);
      }
      return true;
    };

    void (async () => {
      try {
        await delay(BLUFF_JOIN_BUFFER_MS);
        while (!cancelled) {
          const q = await apiClient.postAnswerBluffRoomControl(code, "open_question");
          if (!apply(q)) return;
          const g0 = parseBluffGamePayload(q.game as unknown);
          if (g0?.phase === "game_over") return;
          const a = await apiClient.postAnswerBluffRoomControl(code, "open_answer");
          if (!apply(a)) return;
          const g1 = parseBluffGamePayload(a.game as unknown);
          if (g1?.phase === "game_over") return;
          await delayUntilBluffPhaseEnd(g1, BLUFF_ROUND_MS);
          if (cancelled) return;
          const v = await apiClient.postAnswerBluffRoomControl(code, "open_vote");
          if (!apply(v)) return;
          const gv = parseBluffGamePayload(v.game as unknown);
          if (gv?.phase === "game_over") return;
          await delayUntilBluffPhaseEnd(gv, BLUFF_ROUND_MS);
          if (cancelled) return;
          const c = await apiClient.postAnswerBluffRoomControl(code, "close_vote");
          if (!apply(c)) return;
          await delay(BLUFF_LEADERBOARD_MS);
        }
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? (e as Error)?.message ?? "Request failed";
        if (!cancelled) setControlError(String(msg));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hostGameStarted, room?.code]);

  const sendHostAnswer = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const text = hostAnswerDraft.trim();
    if (!text) return;
    const sg = serverGameRef.current;
    if (!sg || !bluffQaCombinedPhase(sg.phase)) return;
    const truth = resolveBluffCanonicalTruth(room?.questions ?? [], sg);
    if (truth && bluffSubmissionMatchesTruth(text, truth)) {
      setHostAnswerTruthBlockHint(t("party.joinBluff.cannotSubmitTruth"));
      return;
    }
    setHostAnswerTruthBlockHint(null);
    try {
      ws.send(JSON.stringify({ type: "bluff_answer", text }));
      trackEvent(MixpanelEvents.PARTY_BLUFF_HOST_ANSWER_SUBMITTED, {
        round_index: sg.round_index,
        answer_length: text.length,
      });
    } catch {
      setControlError(t("party.joinBluff.errorConnect"));
    }
  };

  const sendHostVote = (choiceId: number) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "bluff_vote", choice_id: choiceId }));
      setHostVoteSubmitted(true);
      trackEvent(MixpanelEvents.PARTY_BLUFF_HOST_VOTE_SUBMITTED, { choice_id: choiceId });
    } catch {
      setControlError(t("party.joinBluff.errorConnect"));
    }
  };

  const deck = room?.questions ?? [];
  const qCount = deck.length;
  const rosterDisplay: BluffRoster =
    serverGame && Object.keys(serverGame.players).length > 0 ? serverGame.players : guestRoster;
  const leaderboardRows = useMemo(() => scoreRows(serverGame), [serverGame]);
  const hostScoreRows = leaderboardRows;
  const topHostName = hostScoreRows[0]?.name;

  const votePhase = serverGame?.phase;
  const voteRoundIndex = serverGame?.round_index;
  const votePhaseEndsAt = serverGame?.phase_ends_at;
  const voteOptionsRaw = serverGame?.vote_options;

  const voteOptionsShuffled = useMemo(() => {
    if (votePhase !== "vote" || !voteOptionsRaw?.length) return [];
    const seed = bluffVoteOptionsShuffleSeed(voteRoundIndex ?? 0, votePhaseEndsAt ?? null, voteOptionsRaw);
    return shuffleBluffVoteOptionsDeterministic(voteOptionsRaw, seed);
  }, [votePhase, voteRoundIndex, votePhaseEndsAt, voteOptionsRaw]);

  const copyJoinLink = useCallback(async () => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setJoinLinkCopied(true);
      trackEvent(MixpanelEvents.PARTY_BLUFF_JOIN_LINK_COPIED, { method: "clipboard_api" });
      window.setTimeout(() => setJoinLinkCopied(false), 2500);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = joinUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setJoinLinkCopied(true);
        trackEvent(MixpanelEvents.PARTY_BLUFF_JOIN_LINK_COPIED, { method: "exec_command" });
        window.setTimeout(() => setJoinLinkCopied(false), 2500);
      } catch {
        setControlError(t("party.playBluff.copyJoinLinkFailed"));
      }
    }
  }, [joinUrl, t]);

  const hostCountdownLabel =
    hostGameStarted && serverGame?.phase_ends_at && phaseSecondsLeft !== null
      ? phaseSecondsLeft > 0
        ? t("party.playBluff.timerSeconds", { count: phaseSecondsLeft })
        : t("party.playBluff.timerEnded")
      : null;

  const bluffTimerLive =
    hostGameStarted && serverGame?.phase_ends_at != null && phaseSecondsLeft != null && phaseSecondsLeft > 0;

  /** Full-width footer sat under answer / vote controls — hide during timed play to avoid mis-taps. */
  const showEndGameFooter =
    !hostGameStarted ||
    !serverGame ||
    (!bluffQaCombinedPhase(serverGame.phase) && serverGame.phase !== "vote");

  if (!room) {
    return (
      <Box sx={{ px: 2.5, pb: 15 }}>
        <PartyTopBar title={t("party.playBluff.titleBar")} onBack={() => navigate("/party/detail/bluff")} />
        <Stack alignItems="center" sx={{ py: 1.5 }}>
          <PartyMascot shellId="bluff" size={100} />
        </Stack>
        <Typography sx={{ color: partyPal.inkDim, fontSize: 14, mb: 2, lineHeight: 1.5 }}>{t("party.playBluff.pickCategoryHint")}</Typography>
        <Typography className="party-display" sx={{ fontSize: 26, mb: 1.5, color: partyPal.ink }}>
          {t("party.playBluff.pickCategoryTitle")}
        </Typography>
        <Stack spacing={1}>
          {(catQ.data ?? []).map((c: ModeCategoryRow) => (
            <Button
              key={c.id}
              type="button"
              disabled={startMut.isPending}
              onClick={() =>
                startMut.mutate({
                  categoryId: c.id,
                  category_names: [modeCategoryPrimaryName(c)],
                })
              }
              className="party-btn party-btn-paper"
              sx={{ textAlign: "start", py: 2, px: 2, fontSize: 16, justifyContent: "flex-start", borderRadius: 2 }}
            >
              {modeCategoryDisplayName(c, i18n.language)}
            </Button>
          ))}
        </Stack>
        {(startMut.error as { response?: { status?: number } })?.response?.status === 402 && (
          <Paper
            elevation={0}
            sx={{ mt: 2, p: 2, borderRadius: 2, border: `1.5px solid ${partyPal.hot}`, bgcolor: "rgba(255,61,120,0.08)" }}
          >
            <Typography sx={{ fontWeight: 800, color: partyPal.ink }}>{t("party.playBluff.insufficientCredits")}</Typography>
          </Paper>
        )}
        <Button
          type="button"
          variant="text"
          onClick={() => setSuggestCategoryOpen(true)}
          sx={{ mt: 1, color: partyPal.hot, fontWeight: 800, textTransform: "none" }}
        >
          {t("party.suggestCategory.cta")}
        </Button>
        <PartySuggestCategoryDrawer
          open={suggestCategoryOpen}
          onClose={() => setSuggestCategoryOpen(false)}
          gameKey="answerBluff"
          partyPal={partyPal}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2.5, pb: 15 }}>
      <PartyTopBar
        title={t("party.playBluff.roomTitleBar")}
        onBack={() => {
          trackEvent(MixpanelEvents.PARTY_BLUFF_HOST_ROOM_LEFT, {});
          hostGameStartedRef.current = false;
          setHostGameStarted(false);
          hostJoinSentRef.current = false;
          setServerGame(null);
          setRoom(null);
        }}
      />

      <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.14em", color: partyPal.inkDim, mb: 0.75 }}>
        {t("party.playBluff.roomCodeLabel")}
      </Typography>
      <Typography className="party-display" sx={{ fontSize: "clamp(36px, 10vw, 52px)", fontWeight: 900, letterSpacing: "0.08em", textAlign: "center", mb: 2 }}>
        {room.code}
      </Typography>

      {joinUrl ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            border: `1.5px solid ${partyPal.inkFaint}`,
            bgcolor: "rgba(255,255,255,0.04)",
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: partyPal.ink }}>{t("party.playBluff.qrCaption")}</Typography>
            <Box sx={{ bgcolor: "#fff", p: 1.5, borderRadius: 2, lineHeight: 0 }}>
              <QRCode value={joinUrl} size={196} level="M" />
            </Box>
            <Typography sx={{ fontSize: 12, color: partyPal.inkDim, textAlign: "center", lineHeight: 1.45, px: 1 }}>
              {t("party.playBluff.qrHint")}
            </Typography>
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 18, color: partyPal.ink }} />}
              onClick={() => void copyJoinLink()}
              className="party-btn party-btn-paper"
              sx={{ mt: 0.5, borderColor: partyPal.inkFaint, color: partyPal.ink, textTransform: "none" }}
            >
              {t("party.playBluff.copyJoinLink")}
            </Button>
            {joinLinkCopied ? (
              <Typography sx={{ fontSize: 12, color: partyPal.hot, fontWeight: 700 }}>{t("party.playBluff.copyJoinLinkDone")}</Typography>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <BluffPlayersRoster
        roster={rosterDisplay}
        title={t("party.playBluff.playersTitle")}
        emptyHint={t("party.playBluff.playersEmpty")}
        partyPal={partyPal}
      />

      <Typography sx={{ color: partyPal.inkDim, fontSize: 14, mb: 2 }}>{t("party.playBluff.loadedQuestions", { count: qCount })}</Typography>

      {!hostGameStarted ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            border: `1.5px solid ${partyPal.inkFaint}`,
            bgcolor: "rgba(255,255,255,0.04)",
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 1.5, color: partyPal.ink }}>{t("party.playBluff.hostNameTitle")}</Typography>
          <Typography sx={{ fontSize: 13, color: partyPal.inkDim, mb: 2, lineHeight: 1.45 }}>{t("party.playBluff.hostNameHint")}</Typography>
          <Stack spacing={2}>
            <TextField
              label={t("party.playBluff.hostDisplayNameLabel")}
              placeholder={t("party.joinBluff.displayNamePlaceholder")}
              value={hostDisplayName}
              onChange={(e) => setHostDisplayName(e.target.value)}
              onKeyDown={(e) =>
                handlePartyRosterNameEnter(e, () => {
                  if (!hostDisplayName.trim() || guestJoinCount < 1) return;
                  handleStartHostGame();
                })
              }
              fullWidth
              autoComplete="nickname"
              sx={fieldSx}
            />
            <Button
              type="button"
              variant="contained"
              className="party-btn party-btn-hot"
              fullWidth
              disabled={!hostDisplayName.trim() || guestJoinCount < 1}
              onClick={handleStartHostGame}
            >
              {t("party.playBluff.startGame")}
            </Button>
            {guestJoinCount < 1 ? (
              <Typography sx={{ fontSize: 13, color: partyPal.hot, textAlign: "center", lineHeight: 1.45, fontWeight: 700 }}>
                {t("party.playBluff.needOneOtherPlayer")}
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            border: `1.5px solid ${partyPal.inkFaint}`,
            bgcolor: "rgba(255,255,255,0.04)",
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 1, color: partyPal.ink }}>{t("party.playBluff.autoRunTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: partyPal.inkDim, lineHeight: 1.45 }}>{t("party.playBluff.autoRunBody")}</Typography>
        </Paper>
      )}

      {controlError ? (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 3,
            border: `1.5px solid rgba(255,200,120,0.5)`,
            bgcolor: "rgba(0,0,0,0.22)",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Typography sx={{ fontWeight: 800, color: partyPal.ink, lineHeight: 1.45, flex: 1 }}>{controlError}</Typography>
            <Button type="button" size="small" onClick={() => setControlError(null)} sx={{ color: partyPal.inkDim, minWidth: "auto", px: 0.5 }}>
              ×
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {hostGameStarted && leaderboardRows.length > 0 ? (
        <BluffScoresStrip rows={leaderboardRows} title={t("party.playBluff.scoresStripTitle")} partyPal={partyPal} />
      ) : null}

      {hostGameStarted ? (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Typography
            className="party-mono"
            sx={{ fontWeight: 800, fontSize: 11, letterSpacing: "0.18em", color: partyPal.gold, textTransform: "uppercase" }}
          >
            {t("party.playBluff.serverPhaseTitle")}
          </Typography>
          {serverGame ? (
            <BluffQuestionCard
              phaseLabel={
                bluffQaCombinedPhase(serverGame.phase) ? bluffPhaseEmojiLabel(t, "qa") : bluffPhaseEmojiLabel(t, serverGame.phase)
              }
              countdown={hostCountdownLabel}
              partyPal={partyPal}
              timerLive={bluffTimerLive}
              questionHighlight={
                bluffQaCombinedPhase(serverGame.phase) && serverGame.current_prompt ? serverGame.current_prompt : null
              }
            >
              <Typography className="party-mono" sx={{ fontSize: 13, fontWeight: 800, color: partyPal.paperInk, opacity: 0.85, mb: 1 }}>
                {t("party.playBluff.roundLine", { n: serverGame.round_index + 1, total: Math.max(qCount, 1) })}
              </Typography>
              {(serverGame.phase === "leaderboard" || serverGame.phase === "game_over") && leaderboardRows.length > 0 ? (
                <BluffLeaderboardList
                  rows={leaderboardRows}
                  partyPal={partyPal}
                  headline={
                    serverGame.phase === "game_over" ? t("party.playBluff.finalLeaderboard") : t("party.playBluff.roundLeaderboard")
                  }
                  winnerLine={
                    serverGame.phase === "game_over" && topHostName ? t("party.joinBluff.winner", { name: topHostName }) : null
                  }
                />
              ) : null}

              {serverGame.phase === "game_over" && leaderboardRows.length > 0 ? (
                <PartyShareResultButton
                  className="party-btn party-btn-paper"
                  sx={{ width: "100%", mt: 1.5 }}
                  model={{
                    shellId: "bluff",
                    modeTitle: shareModeTitle,
                    resultTitle: t("party.playBluff.finalLeaderboard"),
                    hero: topHostName ?? "",
                    sub: topHostName ? t("party.joinBluff.winner", { name: topHostName }) : undefined,
                    standings: leaderboardRows.slice(0, 4).map((r) => ({
                      name: r.name,
                      value: `${r.pts}`,
                    })),
                  }}
                />
              ) : null}
            </BluffQuestionCard>
          ) : (
            <Typography sx={{ fontSize: 14, color: partyPal.inkDim }}>{t("party.playBluff.serverPhaseLoading")}</Typography>
          )}
        </Stack>
      ) : null}

      {hostGameStarted && serverGame ? (
        <BluffQuestionCard phaseLabel={`🎙️ ${t("party.playBluff.yourTurnTitle")}`} partyPal={partyPal}>
          {bluffQaCombinedPhase(serverGame.phase) && (
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  border: `1px solid ${alpha(partyPal.paperInk, 0.12)}`,
                  bgcolor: alpha(partyPal.paperInk, 0.04),
                }}
              >
                <Typography className="party-display" sx={{ fontSize: "clamp(20px, 4.8vw, 26px)", fontWeight: 900, mb: 1, color: partyPal.paperInk }}>
                  {t("party.playBluff.hostLiveTitle")}
                </Typography>
                <Typography sx={{ fontSize: 16, lineHeight: 1.55, fontWeight: 600, color: partyPal.paperInk, opacity: 0.92 }}>
                  {t("party.playBluff.hostLiveBody")}
                </Typography>
              </Box>
              {!hostAnswerSubmitLocked ? (
                <>
                  <Typography className="party-mono" sx={{ fontSize: 12, letterSpacing: "0.14em", color: partyPal.hot, fontWeight: 800 }}>
                    {t("party.joinBluff.typeYourAnswer")}
                  </Typography>
                  <TextField
                    value={hostAnswerDraft}
                    onChange={(e) => {
                      setHostAnswerTruthBlockHint(null);
                      setHostAnswerDraft(e.target.value);
                    }}
                    placeholder={t("party.joinBluff.answerPlaceholder")}
                    fullWidth
                    multiline
                    minRows={2}
                    error={Boolean(hostAnswerTruthBlockHint)}
                    helperText={hostAnswerTruthBlockHint ?? undefined}
                    sx={answerFieldSx}
                  />
                  <Button type="button" variant="contained" className="party-btn party-btn-hot" onClick={sendHostAnswer} disabled={!hostAnswerDraft.trim()}>
                    {t("party.joinBluff.sendAnswer")}
                  </Button>
                </>
              ) : (
                <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.45, color: partyPal.paperInk }}>{t("party.playBluff.answerLockedIn")}</Typography>
              )}
              {hostAnswerAck ? (
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: partyPal.paperInk, opacity: 0.85 }}>{hostAnswerAck}</Typography>
              ) : null}
            </Stack>
          )}
          {serverGame.phase === "vote" && (
            <Stack spacing={2}>
              <Typography className="party-display" sx={{ fontSize: "clamp(18px, 4.2vw, 22px)", fontWeight: 900, textAlign: "center", color: partyPal.paperInk, lineHeight: 1.25 }}>
                {t("party.joinBluff.votePickTruth")}
              </Typography>
              {hostVoteSubmitted ? (
                <Typography
                  className="party-mono"
                  sx={{ color: partyPal.violet, textAlign: "center", fontWeight: 800, fontSize: 15, letterSpacing: "0.06em" }}
                >
                  ✓ {t("party.joinBluff.voteSent")}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {(voteOptionsShuffled.length ? voteOptionsShuffled : (serverGame.vote_options ?? [])).map((opt) => (
                    <Button
                      key={opt.id}
                      type="button"
                      fullWidth
                      variant="outlined"
                      className="party-btn party-btn-paper"
                      onClick={() => sendHostVote(opt.id)}
                      sx={{
                        ...bluffVoteOptionButtonSx(partyPal),
                        whiteSpace: "normal",
                        lineHeight: 1.35,
                        textTransform: "none",
                        fontSize: 16,
                      }}
                    >
                      {opt.text}
                    </Button>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
          {(serverGame.phase === "lobby" || serverGame.phase === "leaderboard" || serverGame.phase === "game_over") && (
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: partyPal.paperInk, lineHeight: 1.5 }}>{t("party.playBluff.yourTurnIdle")}</Typography>
          )}
        </BluffQuestionCard>
      ) : null}

      {showEndGameFooter ? (
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={() => navigate("/")}
          sx={{
            mt: 4,
            mb: 1,
            mx: "auto",
            display: "block",
            color: partyPal.inkDim,
            fontWeight: 700,
            textTransform: "none",
            minHeight: 40,
          }}
        >
          {t("party.playBluff.endGame")}
        </Button>
      ) : null}
    </Box>
  );
}
